import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Shield, 
  Lock, 
  Eye, 
  EyeOff, 
  Loader2, 
  Mail, 
  ArrowRight,
  CheckCircle,
  User,
  Sun,
  Moon,
  Check,
  X,
  Building2,
  TrendingUp,
  PiggyBank
} from 'lucide-react';

// Premium stock images
const IMAGES = {
  registerBg: 'https://images.unsplash.com/photo-1759310610325-2c7cb621e5e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGNvcnBvcmF0ZSUyMHN1Y2Nlc3MlMjBwYXJ0bmVyc2hpcHxlbnwwfHx8fDE3NzIxODAxMjR8MA&ixlib=rb-4.1.0&q=85',
};

export default function RegisterPage() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  
  const { register, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const BrandName = () => {
    const name = branding.brand_name || 'AwadhPay';
    const m = /^(.*?)(pay)$/i.exec(name);
    return m ? <>{m[1]}<span className="text-primary">{m[2]}</span></> : <>{name}</>;
  };

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 25;
    if (password.length >= 12) score += 15;
    if (/[a-z]/.test(password)) score += 15;
    if (/[A-Z]/.test(password)) score += 15;
    if (/[0-9]/.test(password)) score += 15;
    if (/[^a-zA-Z0-9]/.test(password)) score += 15;
    return Math.min(score, 100);
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const getStrengthColor = () => {
    if (passwordStrength < 40) return 'bg-red-500';
    if (passwordStrength < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  const getStrengthLabel = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };

  const passwordRequirements = [
    { label: 'At least 8 characters', met: formData.password.length >= 8 },
    { label: 'Contains uppercase letter', met: /[A-Z]/.test(formData.password) },
    { label: 'Contains lowercase letter', met: /[a-z]/.test(formData.password) },
    { label: 'Contains number', met: /[0-9]/.test(formData.password) },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name || formData.name.length < 2) {
      newErrors.name = 'Please enter your full name';
    }
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await register(formData.name, formData.email, formData.password, 'merchant');
      // If arriving via a reseller signup link, link the new merchant to that reseller
      const code = new URLSearchParams(window.location.search).get('reseller');
      if (code) {
        try { await api.post('/reseller/attach', { reseller_code: code }); } catch (e) { /* non-blocking */ }
      }
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Building2, title: 'Quick Onboarding', desc: 'Get started in minutes with our streamlined KYC process' },
    { icon: TrendingUp, title: 'Real-time Analytics', desc: 'Monitor your business performance with detailed insights' },
    { icon: PiggyBank, title: 'Fast Settlements', desc: 'T+2 settlement cycles with transparent processing' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Premium Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img 
          src={IMAGES.registerBg}
          alt="Business Partnership"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-primary/20" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center border border-primary/30">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <span className="text-2xl font-heading font-bold tracking-tight">
              <BrandName />
            </span>
          </Link>
          
          {/* Middle Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-heading font-bold leading-tight mb-4">
                Start Accepting Payments
                <span className="text-gradient block">In Minutes</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md">
                Join thousands of merchants who trust AwadhPay for their payment infrastructure.
              </p>
            </div>
            
            <div className="space-y-5">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-4 p-4 rounded-lg bg-card/30 backdrop-blur-sm border border-border/30"
                >
                  <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <p className="text-sm text-muted-foreground">
            © 2026 {branding.brand_name}. {branding.description}
          </p>
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="flex items-center justify-between p-6">
          <Link to="/" className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary/20 flex items-center justify-center border border-primary/30">
              <CreditCard className="w-4 h-4 text-primary" />
            </div>
            <span className="text-xl font-heading font-bold">
              <BrandName />
            </span>
          </Link>
          <div className="flex items-center gap-3 ml-auto">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-sm">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <span className="text-sm text-muted-foreground hidden sm:inline">Already registered?</span>
            <Button variant="outline" onClick={() => navigate('/login')} className="rounded-sm" data-testid="goto-login-btn">
              Sign In
            </Button>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-heading font-bold mb-2">Create Your Account</h1>
              <p className="text-muted-foreground">Start accepting payments in minutes</p>
            </div>

            {/* Register Form Card */}
            <Card className="border-border/50 shadow-xl shadow-primary/5">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name Field */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="name" 
                      className={`text-sm transition-colors ${focusedField === 'name' ? 'text-primary' : ''}`}
                    >
                      Full Name
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          if (errors.name) setErrors({ ...errors, name: null });
                        }}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 h-11 rounded-sm transition-all ${
                          errors.name ? 'border-red-500 focus-visible:ring-red-500' : 
                          focusedField === 'name' ? 'border-primary' : ''
                        }`}
                        data-testid="register-name-input"
                      />
                    </div>
                    {errors.name && (
                      <p className="text-xs text-red-500">{errors.name}</p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="email" 
                      className={`text-sm transition-colors ${focusedField === 'email' ? 'text-primary' : ''}`}
                    >
                      Business Email
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="name@company.com"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          if (errors.email) setErrors({ ...errors, email: null });
                        }}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 h-11 rounded-sm transition-all ${
                          errors.email ? 'border-red-500 focus-visible:ring-red-500' : 
                          focusedField === 'email' ? 'border-primary' : ''
                        }`}
                        data-testid="register-email-input"
                      />
                      {formData.email && !errors.email && /\S+@\S+\.\S+/.test(formData.email) && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                      )}
                    </div>
                    {errors.email && (
                      <p className="text-xs text-red-500">{errors.email}</p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="password"
                      className={`text-sm transition-colors ${focusedField === 'password' ? 'text-primary' : ''}`}
                    >
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={(e) => {
                          setFormData({ ...formData, password: e.target.value });
                          if (errors.password) setErrors({ ...errors, password: null });
                        }}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 pr-10 h-11 rounded-sm transition-all ${
                          errors.password ? 'border-red-500 focus-visible:ring-red-500' : 
                          focusedField === 'password' ? 'border-primary' : ''
                        }`}
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    
                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Progress value={passwordStrength} className={`h-1.5 flex-1 ${getStrengthColor()}`} />
                          <span className={`text-xs font-medium ${
                            passwordStrength < 40 ? 'text-red-500' : 
                            passwordStrength < 70 ? 'text-yellow-500' : 'text-green-500'
                          }`}>
                            {getStrengthLabel()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          {passwordRequirements.map((req, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              {req.met ? (
                                <Check className="h-3 w-3 text-green-500" />
                              ) : (
                                <X className="h-3 w-3 text-muted-foreground" />
                              )}
                              <span className={`text-xs ${req.met ? 'text-green-500' : 'text-muted-foreground'}`}>
                                {req.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {errors.password && (
                      <p className="text-xs text-red-500">{errors.password}</p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="confirmPassword"
                      className={`text-sm transition-colors ${focusedField === 'confirmPassword' ? 'text-primary' : ''}`}
                    >
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={(e) => {
                          setFormData({ ...formData, confirmPassword: e.target.value });
                          if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: null });
                        }}
                        onFocus={() => setFocusedField('confirmPassword')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 pr-10 h-11 rounded-sm transition-all ${
                          errors.confirmPassword ? 'border-red-500 focus-visible:ring-red-500' : 
                          focusedField === 'confirmPassword' ? 'border-primary' : ''
                        }`}
                        data-testid="register-confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      {formData.confirmPassword && formData.password === formData.confirmPassword && (
                        <CheckCircle className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                      )}
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-xs text-red-500">{errors.confirmPassword}</p>
                    )}
                  </div>

                  {/* Submit Error */}
                  {errors.submit && (
                    <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/20 text-sm text-red-500">
                      {errors.submit}
                    </div>
                  )}

                  {/* Terms Notice */}
                  <p className="text-xs text-muted-foreground text-center">
                    By creating an account, you agree to our{' '}
                    <Link to="#" className="text-primary hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="#" className="text-primary hover:underline">Privacy Policy</Link>
                  </p>

                  {/* Register Button */}
                  <Button 
                    type="submit" 
                    className="w-full h-11 rounded-sm glow-primary text-base font-semibold" 
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Account...</>
                    ) : (
                      <>Create Account <ArrowRight className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Bottom Link */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
